import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  InputAdornment,
  Pagination,
  Stack,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import emailTemplateService, { EmailTemplate, UserEmailTemplateOverride } from '../../services/emailTemplate.service';
import EmailTemplateEditor from './EmailTemplateEditor';
import EmailTemplatePreview from './EmailTemplatePreview';

interface EmailTemplateManagerProps {
  className?: string;
}

const EmailTemplateManager: React.FC<EmailTemplateManagerProps> = ({ className = '' }) => {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [userOverrides, setUserOverrides] = useState<UserEmailTemplateOverride[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [showEditor, setShowEditor] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState<any>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadTemplates();
    loadUserOverrides();
  }, [currentPage, searchTerm]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await emailTemplateService.getTemplates(currentPage, 50);

      if (response.success) {
        let filteredTemplates = response.data.data;

        if (searchTerm) {
          filteredTemplates = filteredTemplates.filter((template: EmailTemplate) =>
            template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            template.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (template.description || '').toLowerCase().includes(searchTerm.toLowerCase())
          );
        }

        setTemplates(filteredTemplates);
        setTotalPages(response.data.last_page || 1);
      } else {
        setError('Failed to load email templates');
      }
    } catch (err: any) {
      console.error('Load templates error:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load email templates');
    } finally {
      setLoading(false);
    }
  };

  const loadUserOverrides = async () => {
    try {
      const response = await emailTemplateService.getUserOverrides();
      if (response.success) {
        setUserOverrides(response.data);
      }
    } catch (err) {
      console.error('Failed to load user overrides:', err);
    }
  };

  const handleEditTemplate = (template: EmailTemplate) => {
    setSelectedTemplate(template);
    setShowEditor(true);
  };

  const handlePreviewTemplate = async (template: EmailTemplate) => {
    try {
      const sampleVariables = emailTemplateService.generateSampleData(template.variables || []);
      const response = await emailTemplateService.previewTemplate(template.id, sampleVariables);

      if (response.success) {
        setPreviewData({
          subject: response.data.rendered.subject,
          content: response.data.rendered.body,
          variables_used: sampleVariables,
        });
        setShowPreview(true);
      } else {
        throw new Error(response.message || 'Failed to preview template');
      }
    } catch (err: any) {
      const message =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        'Failed to preview template';
      setError(message);
      toast.error(message);
    }
  };

  const handleSaveTemplate = async (templateData: Partial<EmailTemplate>) => {
    try {
      if (selectedTemplate) {
        const response = await emailTemplateService.updateTemplate(selectedTemplate.id, templateData);
        if (response.success) {
          await loadTemplates();
          setShowEditor(false);
          setSelectedTemplate(null);
          toast.success('Template updated successfully');
        } else {
          setError('Failed to save template');
          toast.error('Failed to save template');
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save template');
      toast.error('Failed to save template');
    }
  };

  const handleSaveUserOverride = async (templateId: number, overrideData: any) => {
    try {
      const response = await emailTemplateService.createOrUpdateUserOverride(templateId, overrideData);
      if (response.success) {
        await loadUserOverrides();
        setShowEditor(false);
        setSelectedTemplate(null);
        toast.success('User override saved successfully');
      } else {
        setError('Failed to save user override');
        toast.error('Failed to save user override');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save user override');
      toast.error('Failed to save user override');
    }
  };

  const getUserOverride = (templateId: number): UserEmailTemplateOverride | undefined => {
    return userOverrides.find(override => override.email_template_id === templateId);
  };

  const hasUserOverride = (templateId: number): boolean => {
    return userOverrides.some(override => override.email_template_id === templateId);
  };

  const handleRefresh = () => {
    loadTemplates();
    loadUserOverrides();
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className={className}>
      {/* Header */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          title={
            <Box display="flex" alignItems="center" gap={1}>
              <EmailIcon color="primary" />
              <Typography variant="h4" component="h1">
                Email Templates Management
              </Typography>
            </Box>
          }
          action={
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setShowEditor(true)}
              >
                Create Template
              </Button>
            </Stack>
          }
        />
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Search and Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search templates by name, subject, or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </CardContent>
      </Card>

      {/* Templates Table */}
      <Card>
        <CardHeader
          title={<Typography variant="h6">Email Templates ({templates.length})</Typography>}
        />
        <CardContent>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Template Name</TableCell>
                  <TableCell>Subject</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Variables</TableCell>
                  <TableCell>Last Updated</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {templates
                  .slice((currentPage - 1) * 10, currentPage * 10)
                  .map((template) => {
                    const userOverride = getUserOverride(template.id);
                    const hasOverride = hasUserOverride(template.id);
                    
                    return (
                      <TableRow key={template.id} hover>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="body2" fontWeight="medium">
                              {template.name}
                            </Typography>
                            {hasOverride && (
                              <Chip
                                label="Custom Override"
                                size="small"
                                color="success"
                                variant="outlined"
                              />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap title={userOverride?.subject || template.subject}>
                            {(userOverride?.subject || template.subject).substring(0, 50)}
                            {(userOverride?.subject || template.subject).length > 50 ? '...' : ''}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="textSecondary" noWrap>
                            {template.description || 'No description'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={template.is_active ? 'Active' : 'Inactive'}
                            size="small"
                            color={template.is_active ? 'success' : 'error'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Box display="flex" flexWrap="wrap" gap={0.5}>
                            {template.variables?.slice(0, 3).map((variable, index) => (
                              <Chip
                                key={index}
                                label={`$${variable}`}
                                size="small"
                                variant="outlined"
                                color="primary"
                              />
                            ))}
                            {template.variables && template.variables.length > 3 && (
                              <Chip
                                label={`+${template.variables.length - 3} more`}
                                size="small"
                                variant="outlined"
                                color="default"
                              />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="textSecondary">
                            {formatDate(template.updated_at)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={1}>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleEditTemplate(template)}
                              title="Edit Template"
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="secondary"
                              onClick={() => handlePreviewTemplate(template)}
                              title="Preview Template"
                            >
                              <ViewIcon />
                            </IconButton>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                {templates.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body2" color="textSecondary" py={4}>
                        {searchTerm ? 'No templates found matching your search.' : 'No email templates found.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box display="flex" justifyContent="center" mt={3}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Editor Dialog */}
      {showEditor && (
        <EmailTemplateEditor
          template={selectedTemplate}
          userOverride={selectedTemplate ? getUserOverride(selectedTemplate.id) : undefined}
          onSave={handleSaveTemplate}
          onSaveUserOverride={handleSaveUserOverride}
          onClose={() => {
            setShowEditor(false);
            setSelectedTemplate(null);
          }}
        />
      )}

      {/* Preview Dialog */}
      {showPreview && previewData && (
        <EmailTemplatePreview
          previewData={previewData}
          onClose={() => {
            setShowPreview(false);
            setPreviewData(null);
          }}
        />
      )}
    </Box>
  );
};

export default EmailTemplateManager;